<script setup lang="ts">
import type { MaybeElementRef } from '@vueuse/core';
import { onClickOutside } from '@vueuse/core';
import type { MaybeRefOrGetter } from 'vue';
import { ref } from 'vue';

import {
  GrammarOverlayCard,
  GrammarSwitch,
  GrammarTextField,
} from '@nora-monorepo/grammar-ui';

import { CircleTool } from '#nora-ui/map/drawtools/circle-tool';
import type { DrawTool } from '#nora-ui/map/drawtools/types';

export interface EditingInfoTabProps {
  drawTool: DrawTool;
  ignoredClickOutsideElements?: MaybeRefOrGetter<(MaybeElementRef | string)[]>;
}
const props = withDefaults(defineProps<EditingInfoTabProps>(), {
  ignoredClickOutsideElements: () => [],
});
const emit = defineEmits(['close']);
const { drawTool } = props;
const containerRef = ref(null);
const featureName = ref(drawTool.featureName || '');
const featureNameVisible = ref(drawTool.featureNameVisible);
const showRadiusToggle = drawTool instanceof CircleTool;
const radiusLineVisible = ref(showRadiusToggle ? drawTool.isRadiusLineVisible() : false);

function updateFeatureName(value: string) {
  featureName.value = value;
  drawTool.setFeatureName(value);
}

function toggleFeatureNameVisible(isOn: boolean) {
  featureNameVisible.value = isOn;
  drawTool.setFeatureNameVisible(isOn);
}

function toggleRadiusLineVisible(isOn: boolean) {
  if (showRadiusToggle) {
    radiusLineVisible.value = isOn;
    drawTool.setRadiusLineVisible(isOn);
  }
}

onClickOutside(
  containerRef,
  () => {
    emit('close');
  },
  {
    ignore: props.ignoredClickOutsideElements,
  },
);
</script>

<template>
  <div ref="containerRef">
    <GrammarOverlayCard
      label=""
      class="pointer-events-auto max-w-[320px] medium:max-w-auto"
      style="
        --wordsFormsRadioButtonItemRegularUnselectedMainLeftIconOnlyEnabledWidth: 20px;
        --wordsFormsRadioButtonItemRegularUnselectedMainLeftEnabledMinWidth: 20px;
      "
    >
      <div class="p-1 flex flex-row gap-2">
        <GrammarTextField
          v-model:value="featureName"
          label="Label"
          :placeholder="drawTool.getLabelPlaceholder()"
          @input="updateFeatureName"
        />
        <GrammarSwitch
          label="Show label"
          :selected="featureNameVisible"
          @change="toggleFeatureNameVisible"
        />
        <GrammarSwitch
          v-if="showRadiusToggle"
          label="Show radius"
          :selected="radiusLineVisible"
          @change="toggleRadiusLineVisible"
        />
      </div>
    </GrammarOverlayCard>
  </div>
</template>
